import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  OnOffBadge,
  SkeletonList,
  SkeletonTable,
  Switch,
} from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import { SpeechKitPanel } from './SpeechKitPanel'
import { TaskExtractionPanel } from './TaskExtractionPanel'
import { NextStepSwitchPanel } from '@/nextstep/NextStepSwitchPanel'
import { formatMoney, formatNumber } from '@/lib/format'
import {
  activateAiPromptVersion,
  checkAiConnection,
  createAiPromptVersion,
  fetchAiCalls,
  fetchAiConnection,
  fetchAiPrompts,
  fetchAiUsage,
  switchAiOrganization,
  updateAiConnection,
  type AiCall,
  type AiCheckResult,
  type AiConnection,
  type AiPrompt,
  type AiPromptVersion,
} from './aiApi'

/**
 * ИИ: подключение провайдера, включение на организацию, расход и реестр промптов (тикет #126).
 *
 * Права на экране разные, и каждая половина называет свой отказ словами: подключение, расход и
 * журнал — `system.integration` (тот, кто держит ключ провайдера, не обязан писать промпты),
 * промпты — `system.settings`, как шаблоны писем.
 *
 * Ключ провайдера только записывается: сервер отвечает, задан ли он, но не какой. Пустое поле
 * оставляет прежний ключ, отдельная кнопка его стирает. Стоимость, которую нельзя посчитать
 * (цена за токены не задана), показывается прочерком с пояснением, а не нулём: незаданная цена —
 * не бесплатный вызов.
 */
export function AiSettingsPage() {
  const { t, user } = useApp()
  const mayReadIntegration = can(user, 'system.integration', 'read')
  const mayReadPrompts = can(user, 'system.settings', 'read')
  return (
    <AdminPage titleKey="ai.adm.title" leadKey="ai.adm.lead" forbidden={!mayReadIntegration && !mayReadPrompts}
               readOnly={!can(user, 'system.integration', 'update') && !can(user, 'system.settings', 'update')}>
      {!mayReadIntegration && !mayReadPrompts ? (
        <StateView boxed kind="forbidden" title={t('ai.adm.forbidden')} />
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <ConnectionPanel />
          {mayReadIntegration && <SpeechKitPanel />}
          {mayReadIntegration && <TaskExtractionPanel />}
          {mayReadIntegration && <NextStepSwitchPanel />}
          {mayReadIntegration && <UsagePanel />}
          {mayReadIntegration && <CallsPanel />}
          <PromptsPanel />
        </div>
      )}
    </AdminPage>
  )
}

// ------------------------------------------------------------------ connection

function ConnectionPanel() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayRead = can(user, 'system.integration', 'read')
  const mayEdit = can(user, 'system.integration', 'update')
  const connection = useQuery({
    queryKey: ['admin-ai-connection'],
    queryFn: ({ signal }) => fetchAiConnection(signal),
    enabled: mayRead,
  })
  const [draft, setDraft] = useState<AiConnection | null>(null)
  const [secret, setSecret] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [check, setCheck] = useState<AiCheckResult | null>(null)
  useEffect(() => {
    if (connection.data) setDraft(connection.data)
  }, [connection.data])

  const save = useMutation({
    mutationFn: (clearSecret: boolean) => {
      const value = draft!
      return updateAiConnection({
        enabled: value.enabled,
        provider: value.provider ?? null,
        endpoint: value.endpoint?.trim() || null,
        model: value.model?.trim() || null,
        embeddingModel: value.embeddingModel?.trim() || null,
        folderId: value.folderId?.trim() || null,
        scope: value.scope?.trim() || null,
        authEndpoint: value.authEndpoint?.trim() || null,
        secret: clearSecret ? '' : secret === '' ? undefined : secret,
        userMonthlyTokens: value.userMonthlyTokens ?? null,
        priceInputPer1k: value.priceInputPer1k ?? null,
        priceOutputPer1k: value.priceOutputPer1k ?? null,
        priceEmbeddingPer1k: value.priceEmbeddingPer1k ?? null,
        timeoutSeconds: value.timeoutSeconds ?? null,
      })
    },
    onSuccess: (value) => {
      setNotice(null)
      setSecret('')
      setSaved(true)
      queryClient.setQueryData(['admin-ai-connection'], value)
    },
    onError: (failure) => {
      setSaved(false)
      setNotice(failure instanceof ApiError ? failure.message : t('app.error'))
    },
  })
  const runCheck = useMutation({
    mutationFn: checkAiConnection,
    onSuccess: setCheck,
    onError: (failure) => setCheck({ status: 'rejected', message: failure instanceof ApiError ? failure.message : t('app.error') }),
  })
  const toggleOrganization = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => switchAiOrganization(id, enabled),
    onSuccess: (value) => queryClient.setQueryData(['admin-ai-connection'], value),
    onError: (failure) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  if (!mayRead) {
    return <StateView kind="forbidden" compact title={t('ai.conn.forbidden')} description={t('ai.conn.forbidden.text')} />
  }
  if (connection.isLoading || !draft) {
    return connection.error ? <StateView kind="error" compact title={t('ai.conn.error')} /> : <SkeletonList rows={4} />
  }
  const set = (patch: Partial<AiConnection>) => {
    setSaved(false)
    setDraft({ ...draft, ...patch })
  }
  const numberOrNull = (value: string) => (value.trim() === '' ? null : Number(value))
  const providers = draft.providers ?? []
  const provider = providers.find((candidate) => candidate.code === draft.provider)
  const id = 'ai-conn'
  const organizations = connection.data?.organizations ?? []

  return (
    <>
      <section className="panel stack" style={{ gap: 12 }} aria-label={t('ai.conn.title')}>
        <div className="row" style={{ gap: 8 }}>
          <span className="h2">{t('ai.conn.title')}</span>
          <OnOffBadge on={connection.data?.enabled ?? false} />
        </div>
        {!connection.data?.enabled && (
          <div className="alert alert-warning" role="note">
            <Icon name="alert" />
            <span>{t('ai.conn.off')}</span>
          </div>
        )}
        <div className="alert alert-info" role="note">
          <Icon name="lock" />
          <span>{t('ai.conn.masking')}</span>
        </div>
        <form className="stack" style={{ gap: 12 }} aria-label={t('ai.conn.title')} onSubmit={(event) => {
          event.preventDefault()
          if (mayEdit) save.mutate(false)
        }}>
          <Switch checked={draft.enabled} disabled={!mayEdit} label={t('ai.conn.enabled')} onChange={(value) => set({ enabled: value })} />
          <div className="form-grid">
            <Field label={t('ai.conn.provider')} htmlFor={`${id}-provider`}>
              <select id={`${id}-provider`} className="input" disabled={!mayEdit} value={draft.provider ?? ''}
                      onChange={(e) => set({ provider: e.target.value || null })}>
                <option value="">{t('ai.conn.provider.none')}</option>
                {providers.map((option) => <option key={option.code} value={option.code}>{option.name}</option>)}
              </select>
            </Field>
            <Field label={t('ai.conn.endpoint')} htmlFor={`${id}-endpoint`} help={t('ai.conn.endpoint.help')}>
              <input id={`${id}-endpoint`} className="input mono" disabled={!mayEdit} value={draft.endpoint ?? ''}
                     onChange={(e) => set({ endpoint: e.target.value })} />
            </Field>
            {provider?.requiredOptions.includes('folderId') && (
              <Field label={t('ai.conn.folderId')} htmlFor={`${id}-folder`} required>
                <input id={`${id}-folder`} className="input mono" disabled={!mayEdit} value={draft.folderId ?? ''}
                       onChange={(e) => set({ folderId: e.target.value })} />
              </Field>
            )}
            {draft.provider === 'gigachat' && (
              <>
                <Field label={t('ai.conn.scope')} htmlFor={`${id}-scope`} help={t('ai.conn.scope.help')}>
                  <select id={`${id}-scope`} className="input" disabled={!mayEdit} value={draft.scope ?? 'GIGACHAT_API_PERS'}
                          onChange={(e) => set({ scope: e.target.value })}>
                    {['GIGACHAT_API_PERS', 'GIGACHAT_API_B2B', 'GIGACHAT_API_CORP'].map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t('ai.conn.authEndpoint')} htmlFor={`${id}-auth`} help={t('ai.conn.endpoint.help')}>
                  <input id={`${id}-auth`} className="input mono" disabled={!mayEdit} value={draft.authEndpoint ?? ''}
                         onChange={(e) => set({ authEndpoint: e.target.value })} />
                </Field>
              </>
            )}
            <Field label={t('ai.conn.model')} htmlFor={`${id}-model`} help={t('ai.conn.model.help')}>
              <input id={`${id}-model`} className="input mono" disabled={!mayEdit} value={draft.model ?? ''}
                     onChange={(e) => set({ model: e.target.value })} />
            </Field>
            <Field label={t('ai.conn.embeddingModel')} htmlFor={`${id}-emb`} help={t('ai.conn.model.help')}>
              <input id={`${id}-emb`} className="input mono" disabled={!mayEdit} value={draft.embeddingModel ?? ''}
                     onChange={(e) => set({ embeddingModel: e.target.value })} />
            </Field>
            <Field label={t('ai.conn.secret')} htmlFor={`${id}-secret`}
                   help={draft.secretSet ? t('ai.conn.secret.set') : t('ai.conn.secret.unset')}>
              <input id={`${id}-secret`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                     value={secret} onChange={(e) => {
                       setSaved(false)
                       setSecret(e.target.value)
                     }} />
            </Field>
            <Field label={t('ai.conn.limit')} htmlFor={`${id}-limit`} help={t('ai.conn.limit.help')} required>
              <input id={`${id}-limit`} className="input" type="number" min={1} disabled={!mayEdit}
                     value={draft.userMonthlyTokens ?? ''} onChange={(e) => set({ userMonthlyTokens: numberOrNull(e.target.value) })} />
            </Field>
            <Field label={t('ai.conn.priceInput')} htmlFor={`${id}-price-in`} help={t('ai.conn.price.help')}>
              <input id={`${id}-price-in`} className="input" type="number" min={0} step="0.0001" disabled={!mayEdit}
                     value={draft.priceInputPer1k ?? ''} onChange={(e) => set({ priceInputPer1k: numberOrNull(e.target.value) })} />
            </Field>
            <Field label={t('ai.conn.priceOutput')} htmlFor={`${id}-price-out`}>
              <input id={`${id}-price-out`} className="input" type="number" min={0} step="0.0001" disabled={!mayEdit}
                     value={draft.priceOutputPer1k ?? ''} onChange={(e) => set({ priceOutputPer1k: numberOrNull(e.target.value) })} />
            </Field>
            <Field label={t('ai.conn.priceEmbedding')} htmlFor={`${id}-price-emb`}>
              <input id={`${id}-price-emb`} className="input" type="number" min={0} step="0.0001" disabled={!mayEdit}
                     value={draft.priceEmbeddingPer1k ?? ''} onChange={(e) => set({ priceEmbeddingPer1k: numberOrNull(e.target.value) })} />
            </Field>
            <Field label={t('ai.conn.timeout')} htmlFor={`${id}-timeout`}>
              <input id={`${id}-timeout`} className="input" type="number" min={1} max={600} disabled={!mayEdit}
                     value={draft.timeoutSeconds ?? ''} onChange={(e) => set({ timeoutSeconds: numberOrNull(e.target.value) ?? 60 })} />
            </Field>
          </div>
          <Notice text={notice} />
          {saved && <span className="t-sm muted" role="status">{t('ai.conn.saved')}</span>}
          {check && <CheckOutcome result={check} />}
          {mayEdit && (
            <div className="row" style={{ gap: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                {save.isPending ? t('adm.saving') : t('admin.save')}
              </button>
              <button type="button" className="btn btn-secondary" disabled={runCheck.isPending || !connection.data?.secretSet}
                      onClick={() => {
                        setCheck(null)
                        runCheck.mutate()
                      }}>
                {runCheck.isPending ? t('ai.conn.checking') : t('ai.conn.check')}
              </button>
              {draft.secretSet && (
                <button type="button" className="btn btn-ghost" disabled={save.isPending} onClick={() => save.mutate(true)}>
                  {t('ai.conn.secret.clear')}
                </button>
              )}
            </div>
          )}
        </form>
      </section>

      <section className="panel stack" style={{ gap: 10 }} aria-label={t('ai.org.title')}>
        <span className="h2">{t('ai.org.title')}</span>
        <span className="t-sm muted">{t('ai.org.lead')}</span>
        {organizations.length === 0 ? (
          <StateView kind="empty" compact title={t('ai.org.empty')} />
        ) : (
          <div className="tbl-wrap"><table className="tbl" aria-label={t('ai.org.title')}>
            <thead>
              <tr>
                <th>{t('ai.org.name')}</th>
                <th>{t('ai.org.state')}</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((organization) => (
                <tr key={organization.id}>
                  <td className="strong">{organization.name}</td>
                  <td>
                    {mayEdit ? (
                      <Switch checked={organization.enabled} disabled={toggleOrganization.isPending}
                              ariaLabel={t('ai.org.switch', { name: organization.name })}
                              onChange={(value) => toggleOrganization.mutate({ id: organization.id, enabled: value })} />
                    ) : (
                      <OnOffBadge on={organization.enabled} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </section>
    </>
  )
}

function CheckOutcome({ result }: { result: AiCheckResult }) {
  const { t } = useApp()
  const ok = result.status === 'ok'
  return (
    <div className={ok ? 'alert alert-success' : 'alert alert-danger'} role="status" aria-label={t('ai.check.result')}>
      <Icon name={ok ? 'check' : 'alert'} />
      <span>
        <span className="strong">{t(`ai.check.${result.status}`)}</span>
        {result.model && <> · <span className="mono">{result.model}</span></>}
        {result.message && <> · {result.message}</>}
      </span>
    </div>
  )
}

// ------------------------------------------------------------------ usage and journal

function formatCost(value: number | null | undefined, locale: string): string | null {
  if (value == null) return null
  // Token prices run to fractions of a kopeck: four digits, or a cheap call reads as free.
  return formatMoney(value, 'RUB', locale, { fractionDigits: 4 })
}

function UsagePanel() {
  const { t, locale } = useApp()
  const [month, setMonth] = useState<string>('')
  const usage = useQuery({
    queryKey: ['admin-ai-usage', month],
    queryFn: ({ signal }) => fetchAiUsage(month || null, signal),
  })
  const users = usage.data?.users ?? []
  return (
    <section className="panel stack" style={{ gap: 10 }} aria-label={t('ai.usage.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('ai.usage.title')}</span>
        <span className="spacer" />
        <label className="label" htmlFor="ai-usage-month">{t('ai.usage.month')}</label>
        <input id="ai-usage-month" className="input input-sm" type="month" value={month || usage.data?.month || ''}
               onChange={(e) => setMonth(e.target.value)} style={{ width: 160 }} />
      </div>
      {usage.data?.userMonthlyTokens != null && (
        <span className="t-sm muted">{t('ai.usage.limit', { limit: formatNumber(usage.data.userMonthlyTokens, locale) })}</span>
      )}
      <AdminState isLoading={usage.isLoading} error={usage.error} skeleton={<SkeletonTable columns={6} rows={3} />}>
        {users.length === 0 ? (
          <StateView kind="empty" compact title={t('ai.usage.empty')} />
        ) : (
          <div className="tbl-wrap"><table className="tbl" aria-label={t('ai.usage.title')}>
            <thead>
              <tr>
                <th>{t('ai.usage.user')}</th>
                <th className="num">{t('ai.usage.calls')}</th>
                <th className="num">{t('ai.usage.failures')}</th>
                <th className="num">{t('ai.usage.input')}</th>
                <th className="num">{t('ai.usage.output')}</th>
                <th className="num">{t('ai.usage.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row) => {
                const cost = formatCost(row.cost, locale)
                return (
                  <tr key={row.userId ?? 'system'}>
                    <td>{row.name ?? t('ai.usage.system')}</td>
                    <td className="num">{formatNumber(row.calls, locale)}</td>
                    <td className="num">{formatNumber(row.failures, locale)}</td>
                    <td className="num">{formatNumber(row.inputTokens, locale)}</td>
                    <td className="num">{formatNumber(row.outputTokens, locale)}</td>
                    <td className="num" title={cost ? undefined : t('ai.usage.cost.unknown')}>
                      {cost ?? <span className="muted">{t('ai.cost.unknown')}</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        )}
      </AdminState>
    </section>
  )
}

const CALL_BADGE: Record<AiCall['status'], string> = {
  succeeded: 'badge b-success',
  failed: 'badge b-warning',
  rejected: 'badge b-danger',
  pending: 'badge b-info',
}

function CallsPanel() {
  const { t, locale } = useApp()
  const calls = useQuery({ queryKey: ['admin-ai-calls'], queryFn: ({ signal }) => fetchAiCalls(signal) })
  const list = calls.data ?? []
  return (
    <section className="panel stack" style={{ gap: 10 }} aria-label={t('ai.calls.title')}>
      <span className="h2">{t('ai.calls.title')}</span>
      <span className="t-sm muted">{t('ai.calls.lead')}</span>
      <AdminState isLoading={calls.isLoading} error={calls.error} skeleton={<SkeletonTable columns={7} rows={4} />}>
        {list.length === 0 ? (
          <StateView kind="empty" compact title={t('ai.calls.empty')} />
        ) : (
          <div className="tbl-wrap"><table className="tbl" aria-label={t('ai.calls.title')}>
            <thead>
              <tr>
                <th>{t('ai.calls.at')}</th>
                <th>{t('ai.usage.user')}</th>
                <th>{t('ai.calls.prompt')}</th>
                <th>{t('ai.calls.provider')}</th>
                <th>{t('ai.calls.status')}</th>
                <th className="num">{t('ai.calls.masked')}</th>
                <th className="num">{t('ai.calls.tokens')}</th>
                <th className="num">{t('ai.usage.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((call) => (
                <tr key={call.id}>
                  <td className="t-sm">{new Date(call.createdAt).toLocaleString(locale)}</td>
                  <td>{call.userName ?? t('ai.usage.system')}</td>
                  <td className="mono t-sm">
                    {call.operation === 'embed' ? t('ai.calls.embed') : `${call.promptCode ?? '—'} v${call.promptVersion ?? '?'}`}
                  </td>
                  <td className="t-sm">{call.provider}{call.model && <span className="muted"> · {call.model}</span>}</td>
                  <td className="wrap">
                    <span className={CALL_BADGE[call.status]}>{t(`ai.calls.status.${call.status}`)}</span>
                    {call.error && <div className="t-sm muted wrap">{call.error}</div>}
                  </td>
                  <td className="num">{formatNumber(call.maskedCount, locale)}</td>
                  <td className="num">
                    {call.inputTokens == null ? '—' : formatNumber(call.inputTokens + (call.outputTokens ?? 0), locale)}
                  </td>
                  <td className="num">{formatCost(call.cost, locale) ?? <span className="muted">{t('ai.cost.unknown')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </AdminState>
    </section>
  )
}

// ------------------------------------------------------------------ prompts

function PromptsPanel() {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const mayRead = can(user, 'system.settings', 'read')
  const mayEdit = can(user, 'system.settings', 'update')
  const prompts = useQuery({ queryKey: ['admin-ai-prompts'], queryFn: ({ signal }) => fetchAiPrompts(signal), enabled: mayRead })
  const [editing, setEditing] = useState<{ code: string; base: AiPromptVersion | null } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const activate = useMutation({
    mutationFn: ({ code, version }: { code: string; version: number }) => activateAiPromptVersion(code, version),
    onSuccess: () => {
      setNotice(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-ai-prompts'] })
    },
    onError: (failure) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
  })

  if (!mayRead) {
    return <StateView kind="forbidden" compact title={t('ai.prompts.forbidden')} description={t('ai.prompts.forbidden.text')} />
  }
  const list: AiPrompt[] = prompts.data ?? []
  return (
    <section className="panel stack" style={{ gap: 10 }} aria-label={t('ai.prompts.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('ai.prompts.title')}</span>
        <span className="spacer" />
        {mayEdit && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing({ code: '', base: null })}>
            <Icon name="plus" />{t('ai.prompts.add')}
          </button>
        )}
      </div>
      <span className="t-sm muted">{t('ai.prompts.lead')}</span>
      <Notice text={notice} />
      <AdminState isLoading={prompts.isLoading} error={prompts.error} skeleton={<SkeletonList rows={3} />}>
        {list.length === 0 ? (
          <StateView kind="empty" compact title={t('ai.prompts.empty')} />
        ) : (
          <div className="tbl-wrap"><table className="tbl" aria-label={t('ai.prompts.title')}>
            <thead>
              <tr>
                <th>{t('ai.prompts.code')}</th>
                <th>{t('ai.prompts.active')}</th>
                <th>{t('ai.prompts.variables')}</th>
                <th>{t('ai.prompts.history')}</th>
                {mayEdit && <th aria-label={t('ai.prompts.actions')} />}
              </tr>
            </thead>
            <tbody>
              {list.map((prompt) => {
                const versions = prompt.versions ?? []
                const active = versions.find((version) => version.active) ?? null
                return (
                  <tr key={prompt.code} data-testid={`prompt-${prompt.code}`}>
                    <td className="mono strong">
                      {prompt.code}
                      {active?.description && <div className="t-sm muted">{active.description}</div>}
                    </td>
                    <td>{prompt.activeVersion == null ? '—' : `v${prompt.activeVersion}`}</td>
                    <td className="mono t-sm">{(active?.variables ?? []).map((name) => `{{${name}}}`).join(' ') || '—'}</td>
                    <td>
                      {mayEdit && versions.length > 1 ? (
                        <select className="input input-sm" aria-label={t('ai.prompts.activate', { code: prompt.code })}
                                value={prompt.activeVersion ?? ''} disabled={activate.isPending}
                                onChange={(e) => activate.mutate({ code: prompt.code, version: Number(e.target.value) })}>
                          {versions.map((version) => (
                            <option key={version.version} value={version.version}>
                              {`v${version.version} · ${new Date(version.createdAt).toLocaleDateString(locale)}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        t('ai.prompts.versions', { count: versions.length })
                      )}
                    </td>
                    {mayEdit && (
                      <td>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing({ code: prompt.code, base: active })}>
                          <Icon name="edit" />{t('ai.prompts.newVersion')}
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        )}
      </AdminState>
      {editing && (
        <PromptDialog code={editing.code} base={editing.base} onClose={() => setEditing(null)} onSaved={() => {
          setEditing(null)
          void queryClient.invalidateQueries({ queryKey: ['admin-ai-prompts'] })
        }} />
      )}
    </section>
  )
}

function PromptDialog({ code: initialCode, base, onClose, onSaved }: {
  code: string
  base: AiPromptVersion | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [code, setCode] = useState(initialCode)
  const [description, setDescription] = useState(base?.description ?? '')
  const [systemText, setSystemText] = useState(base?.systemText ?? '')
  const [userTemplate, setUserTemplate] = useState(base?.userTemplate ?? '')
  const [format, setFormat] = useState<'text' | 'json'>(base?.responseFormat === 'JSON' ? 'json' : 'text')
  const [temperature, setTemperature] = useState(base?.temperature == null ? '' : String(base.temperature))
  const [maxTokens, setMaxTokens] = useState(base?.maxTokens == null ? '' : String(base.maxTokens))
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => createAiPromptVersion(code.trim(), {
      description: description.trim() || null,
      systemText: systemText.trim() || null,
      userTemplate,
      responseFormat: format,
      temperature: temperature.trim() === '' ? null : Number(temperature),
      maxTokens: maxTokens.trim() === '' ? null : Number(maxTokens),
    }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof ApiError ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="sheet"
      wide
      overline={t('ai.prompts.title')}
      title={initialCode ? t('ai.prompts.newVersionOf', { code: initialCode }) : t('ai.prompts.add')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending} form={formId}
                            disabled={code.trim() === '' || userTemplate.trim() === ''} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('ai.prompts.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          <Field label={t('ai.prompts.code')} htmlFor={`${formId}-code`} required help={t('ai.prompts.code.help')}>
            <input id={`${formId}-code`} className="input mono" maxLength={64} value={code} readOnly={initialCode !== ''}
                   onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label={t('ai.prompts.format')} htmlFor={`${formId}-format`}>
            <select id={`${formId}-format`} className="input" value={format} onChange={(e) => setFormat(e.target.value as 'text' | 'json')}>
              <option value="text">{t('ai.prompts.format.text')}</option>
              <option value="json">{t('ai.prompts.format.json')}</option>
            </select>
          </Field>
          <Field label={t('ai.prompts.temperature')} htmlFor={`${formId}-temp`}>
            <input id={`${formId}-temp`} className="input" type="number" min={0} max={1} step="0.05" value={temperature}
                   onChange={(e) => setTemperature(e.target.value)} />
          </Field>
          <Field label={t('ai.prompts.maxTokens')} htmlFor={`${formId}-max`}>
            <input id={`${formId}-max`} className="input" type="number" min={1} max={32000} value={maxTokens}
                   onChange={(e) => setMaxTokens(e.target.value)} />
          </Field>
        </div>
        <Field label={t('ai.prompts.description')} htmlFor={`${formId}-desc`}>
          <input id={`${formId}-desc`} className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label={t('ai.prompts.system')} htmlFor={`${formId}-system`}>
          <textarea id={`${formId}-system`} className="input" rows={4} value={systemText} onChange={(e) => setSystemText(e.target.value)} />
        </Field>
        <Field label={t('ai.prompts.user')} htmlFor={`${formId}-user`} required help={t('ai.prompts.user.help')}>
          <textarea id={`${formId}-user`} className="input mono" rows={8} value={userTemplate} onChange={(e) => setUserTemplate(e.target.value)} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
